"use client";

import { MessageSquare } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type AccountMergeReview,
  cancelPhoneAccountMerge,
  confirmPhoneAccountMerge,
  finalizePhoneAuth,
  getPendingPhoneAccountMerge,
  precheckPhoneOtpSend,
  resendPhoneOtp,
  retryPhoneAccountMergePreparation,
  verifyPhoneAttachment,
} from "@/actions/phone-auth";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useSessionHandoff } from "@/hooks/use-session-handoff";
import {
  authAttributionProperties,
  updateAuthAttribution,
} from "@/lib/analytics/auth-attribution";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { authClient } from "@/lib/auth-client";
import type { PostAuthIntent } from "@/lib/auth-redirect";
import {
  PHONE_CREDENTIAL_DEFAULT_COUNTRY,
  parsePhoneCredential,
  phoneCredentialRejectionMessage,
} from "@/lib/identity/phone-credential";
import {
  listenForAndroidSmsCode,
  startAndroidSmsRetriever,
  stopAndroidSmsRetriever,
} from "@/lib/native/sms-retriever";
import { describeOtpVerifyError } from "@/lib/otp/verify-feedback";
import {
  AccountMergeReceiptScreen,
  AccountMergeReviewScreen,
} from "./account-merge-review";
import { OtpCodeInput } from "./otp-code-input";

/**
 * The two-step phone sign-in, as a component rather than a screen.
 *
 * It never navigates. That is the substantive difference from the OAuth buttons:
 * Web Google and Apple hand the browser to another origin and come back through
 * `/auth/complete`, which is why a checkout has to stash its basket before starting one
 * — the React tree is about to be destroyed. Here, `sendOtp` and `verify` are two
 * fetches, the session cookie appears in place, and whatever is on screen stays
 * on screen. So this drops into the global auth drawer, the invite page, and the
 * payment sheet without any of them needing a resume path.
 *
 * The flip side is that the HTTP completion route never runs, so the shared
 * post-auth finalizer is called by `finalizePhoneAuth` after a successful verify.
 *
 * The other flip side, and the subtler one: nothing is thrown away either. The
 * React Query identity and the client router's signed-out RSC payloads both
 * survive a sign-in that never reloaded the document, so the host page carries
 * on rendering its signed-out branch — which reads to the person who just typed
 * a correct code as "it asked me to log in again". `useSessionHandoff` is what
 * makes the new cookie visible to both caches; see the checkout audit in
 * `plans/phone-auth-launch-audit.md`.
 */

export type PhoneAuthFormMode =
  /** No session yet: verifying signs in or signs up. */
  | { kind: "sign-in"; intent?: PostAuthIntent }
  /** Session exists: verifying attaches the number to it. */
  | { kind: "attach"; isReplacement: boolean };

export type PhoneAuthFormProps = {
  mode: PhoneAuthFormMode;
  /** Referral code to attribute, when this form is on an invite surface. */
  referralCode?: string | null;
  /** Where to send the person afterwards. Ignored for `checkout` and `attach`. */
  next?: string | null;
  /**
   * Called once the credential is established. `destination` is null when the
   * caller should stay exactly where it is (checkout, or an attach).
   */
  onSuccess: (result: {
    phoneNumber: string;
    destination: string | null;
  }) => void;
  onCancel?: () => void;
  /** Rendered under the code field, e.g. the checkout's reassurance line. */
  footer?: React.ReactNode;
  /**
   * Told to the parent whenever the step changes, so a surrounding panel can
   * drop its "OR / continue with Google" block once someone is entering a code.
   * Keeping those visible mid-verification invites a tap that abandons the code
   * already sitting in their SMS inbox.
   */
  onStepChange?: (step: "number" | "code") => void;
  autoFocus?: boolean;
};

const CODE_LENGTH = 6;
// Thirty seconds, matching what the apps this screen is modelled on do. Long
// enough that "resend" is not the first thing somebody reaches for while the
// first SMS is still in flight, which is where duplicate charges come from.
// (Rare DLT-delayed SMS can still take longer; the retry re-sends the same
// code, so a late first SMS stays valid.) Send spend is capped server-side
// regardless (3 sends per number per 15 minutes).
const RESEND_COOLDOWN_SECONDS = 30;

export function PhoneAuthForm({
  mode,
  referralCode,
  next,
  onSuccess,
  onCancel,
  footer,
  onStepChange,
  autoFocus = true,
}: PhoneAuthFormProps) {
  const [step, setStep] = useState<
    "number" | "code" | "preparing" | "merge" | "merged"
  >("number");
  const [rawNumber, setRawNumber] = useState("");
  const [canonicalNumber, setCanonicalNumber] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // Set when the code has already been spent, so the UI stops inviting retries
  // of a value the provider has forgotten.
  const [needsNewCode, setNeedsNewCode] = useState(false);
  const [mergeReview, setMergeReview] = useState<{
    intentId: string;
    previewHash: string;
    expiresAt: number;
    preview: AccountMergeReview;
    changedSinceReview: boolean;
    supportCaseId: string | null;
  } | null>(null);
  const [preparingMerge, setPreparingMerge] = useState<{
    intentId: string;
    expiresAt: number;
    phoneTail: string;
    message: string;
  } | null>(null);
  const [mergeDecisions, setMergeDecisions] = useState<
    Record<string, "primary" | "secondary">
  >({});
  const [isMerging, setIsMerging] = useState(false);
  /** A re-check ran and the same conditions still hold, so say so plainly. */
  const [recheckUnchanged, setRecheckUnchanged] = useState(false);
  // Kept after a successful merge so the receipt survives the render that
  // clears the review, and so a retried confirmation shows the same one.
  const [mergeReceipt, setMergeReceipt] = useState<{
    phoneNumber: string;
    receiptId: string;
    repairsPending: number;
    preview: AccountMergeReview;
  } | null>(null);
  const handOffSession = useSessionHandoff();

  useEffect(() => {
    if (mode.kind !== "attach") return;
    // Deliberately no "already checked" ref. There used to be one, and paired
    // with the `cancelled` flag below it made the resume silently impossible to
    // reach: React's development double-invoke ran the effect, tore it down
    // (setting `cancelled`), then re-ran it — where the ref returned early and
    // nothing ever asked again. The probe is a read, so re-running it costs one
    // query and is the only version that cannot lose the race.
    let cancelled = false;
    const applyPending = (
      result: Awaited<ReturnType<typeof getPendingPhoneAccountMerge>>,
    ) => {
      if (cancelled || !result.ok || result.data.state === "none") return;
      if (result.data.state === "merge_preparing") {
        setPreparingMerge(result.data);
        setStep("preparing");
        return;
      }
      setMergeReview({
        intentId: result.data.intentId,
        previewHash: result.data.previewHash,
        expiresAt: result.data.expiresAt,
        preview: result.data.preview,
        changedSinceReview: false,
        supportCaseId: result.data.supportCaseId,
      });
      setMergeDecisions(
        Object.fromEntries(
          result.data.preview.profile.conflicts.map((conflict) => [
            conflict.key,
            conflict.selected,
          ]),
        ),
      );
      setStep("merge");
    };
    // A resume probe must never take the drawer down with it: the number can
    // still be added even if there is no way to look up a pending review.
    void getPendingPhoneAccountMerge().then(applyPending, () => {});
    return () => {
      cancelled = true;
    };
  }, [mode.kind]);

  useEffect(() => {
    onStepChange?.(step === "number" ? "number" : "code");
  }, [onStepChange, step]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    let disposed = false;
    let remove: (() => Promise<void>) | undefined;
    void listenForAndroidSmsCode((retrievedCode) => {
      if (!disposed && /^\d{6}$/.test(retrievedCode)) setCode(retrievedCode);
    }).then((handle) => {
      if (!handle) return;
      if (disposed) void handle.remove();
      else remove = () => handle.remove();
    });
    return () => {
      disposed = true;
      void remove?.();
      void stopAndroidSmsRetriever().catch(() => undefined);
    };
  }, []);

  const sendCode = useCallback(
    async (canonical: string) => {
      setIsSending(true);
      setError(null);
      setNeedsNewCode(false);
      try {
        // Start listening before the provider request so a fast SMS cannot beat
        // the native receiver. Failure is non-blocking; manual entry remains.
        await startAndroidSmsRetriever().catch(() => undefined);
        // Pre-flight applies the per-user send budget and the launch flag. The
        // number/IP/global budgets live inside the auth route itself.
        const precheck = await precheckPhoneOtpSend({
          phoneNumber: canonical,
          // Attaching has refusals a sign-in does not, and all of them are
          // knowable now rather than after the SMS.
          intent: mode.kind,
        });
        if (!precheck.ok) {
          setError(precheck.message);
          return false;
        }

        const sent = await authClient.phoneNumber.sendOtp({
          phoneNumber: canonical,
        });
        if (sent.error) {
          setError(
            sent.error.message ??
              "We couldn't text a code. Try again in a moment.",
          );
          return false;
        }

        captureClientEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_REQUESTED, {
          surface: mode.kind,
          is_resend: false,
        });
        setResendIn(RESEND_COOLDOWN_SECONDS);
        setCode("");
        setStep("code");
        return true;
      } catch {
        setError("We couldn't text a code. Check your connection and retry.");
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [mode.kind],
  );

  /**
   * Resend the SAME code rather than generating a new one.
   *
   * The send endpoint would issue a fresh code and invalidate the one already in
   * transit — unhelpful when the person tapping "resend" may be looking at the
   * first SMS. Falls back to a fresh send if the retry is refused, so the button
   * always does something.
   */
  const resendCode = useCallback(
    async (canonical: string) => {
      setIsSending(true);
      setError(null);
      try {
        await startAndroidSmsRetriever().catch(() => undefined);
        const result = await resendPhoneOtp({ phoneNumber: canonical });
        if (result.ok) {
          // A resend re-texts the SAME code (see `resendPhoneOtp`), so this is
          // tagged apart from a first send: "requested twice" in the funnel
          // usually means "tapped resend", not "started over".
          captureClientEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_REQUESTED, {
            surface: mode.kind,
            is_resend: true,
          });
          setResendIn(RESEND_COOLDOWN_SECONDS);
          return;
        }
        await sendCode(canonical);
      } finally {
        setIsSending(false);
      }
    },
    [mode.kind, sendCode],
  );

  const handleNumberSubmit = useCallback(async () => {
    const parsed = parsePhoneCredential(
      rawNumber,
      PHONE_CREDENTIAL_DEFAULT_COUNTRY,
    );
    if (!parsed.ok) {
      setError(phoneCredentialRejectionMessage(parsed.reason));
      return;
    }

    // Phone is a sign-in provider like the other two, and it never said so:
    // `auth_provider_selected` was fired by the Google and Apple buttons only,
    // so the provider mix silently excluded the option this drawer leads with.
    // Fired on a valid number rather than on every keystroke or rejection.
    updateAuthAttribution({ provider: "phone" });
    const providerProperties = {
      provider: "phone",
      surface: mode.kind,
      ...authAttributionProperties(),
    };
    captureClientEvent(ANALYTICS_EVENTS.SIGNUP_STARTED, providerProperties);
    captureClientEvent(
      ANALYTICS_EVENTS.AUTH_PROVIDER_SELECTED,
      providerProperties,
    );
    captureClientEvent(ANALYTICS_EVENTS.LOGIN_CLICKED, providerProperties);

    setCanonicalNumber(parsed.canonical);
    await sendCode(parsed.canonical);
  }, [mode.kind, rawNumber, sendCode]);

  // `useRef` for the in-flight guard rather than `isVerifying`: `onComplete`
  // fires from an effect, so reading the state it is racing would be stale.
  const verifyInFlight = useRef(false);

  // Focus by ref rather than the `autoFocus` attribute. Inside a vaul drawer the
  // entrance transition steals focus on the first frame, so the attribute is
  // silently undone; two frames later the drawer has settled.
  const numberInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!autoFocus || step !== "number") return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => numberInputRef.current?.focus());
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFocus, step]);

  const handleVerify = useCallback(
    async (submitted: string) => {
      if (!canonicalNumber || verifyInFlight.current) return;
      verifyInFlight.current = true;
      setIsVerifying(true);
      setError(null);

      // Set once a navigation is under way, so the `finally` leaves the spinner
      // alone. A client-side `push` takes a gate round-trip to commit, and
      // dropping the spinner the moment it is issued put "Continue" back to
      // idle over a page that was already leaving — the loader stopped, nothing
      // moved, and a second later the screen changed. Unmounting ends it.
      let leaving = false;

      try {
        if (mode.kind === "attach") {
          const attachment = await verifyPhoneAttachment({
            phoneNumber: canonicalNumber,
            code: submitted,
          });
          if (!attachment.ok) {
            captureClientEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_FAILED, {
              surface: mode.kind,
              failure_code: attachment.code ?? "unknown",
            });
            setError(attachment.message);
            if (attachment.code === "OTP_INVALID") {
              setCode("");
            } else {
              // The OTP was valid and therefore consumed before a policy or
              // persistence refusal — or it expired, in which case retrying it
              // can never succeed. Either way, do not silently retry a dead
              // proof: the resend path issues a fresh one.
              setNeedsNewCode(true);
            }
            return;
          }

          if (attachment.data.state === "merge_preparing") {
            setPreparingMerge(attachment.data);
            setCode("");
            setNeedsNewCode(false);
            setStep("preparing");
            return;
          }

          if (attachment.data.state === "merge_required") {
            setMergeReview({
              intentId: attachment.data.intentId,
              previewHash: attachment.data.previewHash,
              expiresAt: attachment.data.expiresAt,
              preview: attachment.data.preview,
              changedSinceReview: false,
              supportCaseId: attachment.data.supportCaseId,
            });
            setMergeDecisions(
              Object.fromEntries(
                attachment.data.preview.profile.conflicts.map((conflict) => [
                  conflict.key,
                  conflict.selected,
                ]),
              ),
            );
            setCode("");
            setNeedsNewCode(false);
            setStep("merge");
            return;
          }

          onSuccess({ phoneNumber: canonicalNumber, destination: null });
          return;
        }

        const result = await authClient.phoneNumber.verify({
          phoneNumber: canonicalNumber,
          code: submitted,
        });

        if (result.error) {
          // Expired and wrong render differently: a dead code must resend
          // (`needsNewCode` blocks retries and arms the SMS button), a wrong
          // one clears the boxes for another try. See verify-feedback.ts.
          const feedback = describeOtpVerifyError(result.error);
          captureClientEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_FAILED, {
            surface: mode.kind,
            failure_code: feedback.failureCode ?? "unknown",
          });
          setError(feedback.message);
          if (feedback.needsNewCode) {
            setNeedsNewCode(true);
          } else {
            setCode("");
          }
          return;
        }

        // The session cookie exists now. Finish the shared post-auth policy in
        // place — and surface a failure rather than leaving "signed in" UI on
        // top of an anonymous or only partially finalized session.
        const finalized = await finalizePhoneAuth({
          intent: mode.intent ?? "default",
          referralCode: referralCode ?? null,
          next: next ?? null,
        });
        if (!finalized.ok) {
          setError(finalized.message);
          return;
        }

        // Both caches still say "anonymous" and both have to be dropped — see
        // the note at the top of this file. What changes is *when*, and it is
        // the difference between a smooth hand-off and a visible one.
        //
        //   Leaving (`destination` set): drop the caches first. A `push` into a
        //   destination the router prefetched while signed out would otherwise
        //   render an anonymous copy of it.
        //
        //   Staying (`destination` null — a checkout): tell the host FIRST. It
        //   raises its "Finishing your booking" cover in the same commit that
        //   closes this drawer, and the refresh then happens underneath it.
        //   Refreshing first meant the person watched the picker they had just
        //   finished with sit there for a round-trip before anything moved.
        //
        // Deliberately not done for `attach` at all: that leaves the session
        // alone, and a `router.refresh()` underneath an open payment sheet
        // re-renders the server components the sheet was built from.
        const destination = finalized.data.destination;

        if (destination === null) {
          onSuccess({ phoneNumber: canonicalNumber, destination });
          handOffSession();
          return;
        }

        leaving = true;
        handOffSession();
        onSuccess({ phoneNumber: canonicalNumber, destination });
      } catch {
        leaving = false;
        setError("Something went wrong. Please try again.");
      } finally {
        verifyInFlight.current = false;
        if (!leaving) setIsVerifying(false);
      }
    },
    [canonicalNumber, handOffSession, mode, next, onSuccess, referralCode],
  );

  const handleMergeCancel = useCallback(async () => {
    const intentId = mergeReview?.intentId ?? preparingMerge?.intentId;
    setIsMerging(true);
    try {
      if (intentId) await cancelPhoneAccountMerge({ intentId });
    } finally {
      setIsMerging(false);
      setMergeReview(null);
      setPreparingMerge(null);
      setMergeDecisions({});
      setCode("");
      setError(null);
      setStep("number");
      onCancel?.();
    }
  }, [mergeReview?.intentId, onCancel, preparingMerge?.intentId]);

  const handleMergeConfirm = useCallback(async () => {
    if (!mergeReview || isMerging) return;
    setIsMerging(true);
    setError(null);
    try {
      const result = await confirmPhoneAccountMerge({
        intentId: mergeReview.intentId,
        previewHash: mergeReview.previewHash,
        decisions: { profile: mergeDecisions },
      });
      if (!result.ok) {
        setError(result.message);
        return;
      }
      if (result.data.state === "merged") {
        setMergeReceipt({
          phoneNumber: result.data.phoneNumber,
          receiptId: result.data.receiptId,
          repairsPending: result.data.repairsPending,
          preview: result.data.preview,
        });
        setMergeReview(null);
        setStep("merged");
        return;
      }

      setMergeReview({
        intentId: result.data.intentId,
        previewHash: result.data.previewHash,
        expiresAt: result.data.expiresAt,
        preview: result.data.preview,
        changedSinceReview: result.data.state === "preview_changed",
        supportCaseId: result.data.supportCaseId,
      });
      setMergeDecisions(
        Object.fromEntries(
          result.data.preview.profile.conflicts.map((conflict) => [
            conflict.key,
            conflict.selected,
          ]),
        ),
      );
      if (result.data.state === "blocked") {
        setError(
          "Nothing was combined. Support needs to review the overlapping account data shown above.",
        );
      }
    } catch {
      setError("Nothing was combined. Check your connection and try again.");
    } finally {
      setIsMerging(false);
    }
  }, [isMerging, mergeDecisions, mergeReview]);

  const retryMergePreparation = useCallback(async () => {
    if (!preparingMerge || isMerging) return;
    setIsMerging(true);
    setError(null);
    try {
      const result = await retryPhoneAccountMergePreparation({
        intentId: preparingMerge.intentId,
      });
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setMergeReview({
        intentId: result.data.intentId,
        previewHash: result.data.previewHash,
        expiresAt: result.data.expiresAt,
        preview: result.data.preview,
        changedSinceReview: false,
        supportCaseId: result.data.supportCaseId,
      });
      setMergeDecisions(
        Object.fromEntries(
          result.data.preview.profile.conflicts.map((conflict) => [
            conflict.key,
            conflict.selected,
          ]),
        ),
      );
      setPreparingMerge(null);
      setStep("merge");
    } catch {
      setError(
        "Your number is still verified. Check your connection and retry the review.",
      );
    } finally {
      setIsMerging(false);
    }
  }, [isMerging, preparingMerge]);

  /**
   * Rebuild a review that stopped, without starting the flow again.
   *
   * A stop is usually a condition with a clock on it — money held against an
   * order that has not settled, a refund still moving — and the old screen's
   * only exit was "contact support". Re-running the same simulation is what
   * actually answers "is it clear yet?", so the person can close the loop
   * themselves the moment it is.
   */
  const recheckMergeReview = useCallback(async () => {
    if (!mergeReview || isMerging) return;
    setIsMerging(true);
    setError(null);
    setRecheckUnchanged(false);
    try {
      const result = await retryPhoneAccountMergePreparation({
        intentId: mergeReview.intentId,
      });
      if (!result.ok) {
        setError(result.message);
        return;
      }
      const stillBlocked = result.data.preview.blockers.length > 0;
      setMergeReview({
        intentId: result.data.intentId,
        previewHash: result.data.previewHash,
        expiresAt: result.data.expiresAt,
        preview: result.data.preview,
        changedSinceReview: false,
        supportCaseId: result.data.supportCaseId,
      });
      setMergeDecisions(
        Object.fromEntries(
          result.data.preview.profile.conflicts.map((conflict) => [
            conflict.key,
            conflict.selected,
          ]),
        ),
      );
      setRecheckUnchanged(stillBlocked);
    } catch {
      setError(
        "We could not check just now. Your number stays verified — try again in a moment.",
      );
    } finally {
      setIsMerging(false);
    }
  }, [isMerging, mergeReview]);

  if (step === "preparing" && preparingMerge) {
    return (
      <div className="flex w-full flex-col gap-5">
        <div className="space-y-1.5">
          <h2 className="text-xl font-semibold tracking-tight text-text-primary dark:text-white">
            Your number is verified
          </h2>
          <p className="text-sm leading-5 text-text-secondary dark:text-white/60">
            The mobile number ending in {preparingMerge.phoneTail} belongs to
            another Hiive account. We still have your verification proof; no new
            OTP is needed.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-surface-alt p-4 text-sm leading-5 text-text-secondary dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
          {preparingMerge.message}
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button
          type="button"
          disabled={isMerging || preparingMerge.expiresAt <= Date.now()}
          onClick={() => void retryMergePreparation()}
          className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45"
        >
          {isMerging ? (
            <Spinner className="size-5" />
          ) : (
            "Prepare account review"
          )}
        </Button>
        <button
          type="button"
          disabled={isMerging}
          onClick={() => void handleMergeCancel()}
          className="h-10 text-sm text-text-secondary underline underline-offset-4"
        >
          Keep accounts separate
        </button>
      </div>
    );
  }

  if (step === "merged" && mergeReceipt) {
    return (
      <AccountMergeReceiptScreen
        onDone={() =>
          onSuccess({
            phoneNumber: mergeReceipt.phoneNumber,
            destination: null,
          })
        }
        phoneNumber={mergeReceipt.phoneNumber}
        receiptId={mergeReceipt.receiptId}
        repairsPending={mergeReceipt.repairsPending}
        review={mergeReceipt.preview}
      />
    );
  }

  if (step === "merge" && mergeReview) {
    return (
      <AccountMergeReviewScreen
        busy={isMerging}
        changedSinceReview={mergeReview.changedSinceReview}
        decisions={mergeDecisions}
        error={error}
        expiresAt={mergeReview.expiresAt}
        onCancel={() => void handleMergeCancel()}
        onConfirm={() => void handleMergeConfirm()}
        onDecisionChange={(key, choice) => {
          setError(null);
          setMergeDecisions((current) => ({ ...current, [key]: choice }));
        }}
        onRecheck={() => void recheckMergeReview()}
        recheckUnchanged={recheckUnchanged}
        review={mergeReview.preview}
        reviewKey={mergeReview.previewHash}
        supportCaseId={mergeReview.supportCaseId}
      />
    );
  }

  if (step === "number") {
    // Valid before submit, so `Continue` can be disabled rather than accepting a
    // tap and then rejecting it. `parsePhoneCredential` is the same predicate the
    // server accepts, so the button's enabled state cannot disagree with it.
    const parsedNumber = parsePhoneCredential(
      rawNumber,
      PHONE_CREDENTIAL_DEFAULT_COUNTRY,
    );

    return (
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleNumberSubmit();
        }}
      >
        <div className="flex gap-2">
          {/*
            +91 is a fixed chip, deliberately WITHOUT the country-picker chevron
            this pattern usually carries. Only Indian numbers can be used as a
            credential — every other country needs its own DLT registration, SMS
            pricing and number-recycling recovery story — so a chevron would
            advertise a picker that opens onto one option.
          */}
          <span className="flex h-14 shrink-0 items-center gap-2 rounded-2xl border border-border bg-surface-alt px-4 text-base font-semibold text-text-primary dark:border-white/[0.14] dark:bg-white/[0.04] dark:text-white">
            <span aria-hidden="true" className="text-lg leading-none">
              🇮🇳
            </span>
            +91
          </span>

          <input
            ref={numberInputRef}
            id="phone-auth-number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            // The label is the placeholder here, matching the pattern this
            // screen is modelled on. `aria-label` keeps it announced once the
            // field has a value and the placeholder is gone.
            aria-label="Mobile number"
            value={rawNumber}
            onChange={(event) => {
              setError(null);
              setRawNumber(event.target.value.replace(/[^\d\s-]/g, ""));
            }}
            placeholder="Enter mobile number"
            aria-invalid={Boolean(error)}
            className="h-14 min-w-0 flex-1 rounded-2xl border border-border bg-surface-alt px-4 text-base font-medium text-text-primary outline-none placeholder:font-normal placeholder:text-text-tertiary focus:border-brand dark:border-white/[0.14] dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/40 dark:focus:border-white/40"
          />
        </div>

        {error ? (
          <p role="alert" className="px-1 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {mode.kind === "attach" ? (
          <p className="px-1 text-xs leading-4 text-text-tertiary">
            This number will be attached to the Hiive account you are using now.
            It will not create another account.
          </p>
        ) : null}

        <Button
          type="submit"
          // Disabled until the number is one we would actually accept. A tap
          // that spends an SMS on a number we were always going to reject is
          // worse than a button that waits.
          disabled={isSending || !parsedNumber.ok}
          className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
        >
          {isSending ? <Spinner className="size-5" /> : "Continue"}
        </Button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="py-1 text-center text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary"
          >
            {mode.kind === "attach"
              ? "Cancel"
              : "Use a different sign-in method"}
          </button>
        ) : null}
      </form>
    );
  }

  const displayNumber = canonicalNumber
    ? // `+919330000666` -> `+91 9330000666`. Shown in full, not masked: they
      // typed it seconds ago, and seeing it back is how a typo gets caught
      // before five attempts are spent on a code that went to a stranger.
      `${canonicalNumber.slice(0, 3)} ${canonicalNumber.slice(3)}`
    : null;

  const canResend = resendIn <= 0 || needsNewCode;

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-1">
        <p className="text-lg font-semibold tracking-tight text-text-primary">
          OTP Verification
        </p>
        <p className="text-sm text-text-secondary dark:text-white/60">
          We have sent a verification code to{" "}
          <span className="font-medium text-text-primary dark:text-white">
            {displayNumber ?? "your number"}
          </span>
        </p>
      </div>

      <OtpCodeInput
        value={code}
        onChange={(value) => {
          setError(null);
          setCode(value);
        }}
        onComplete={(value) => {
          if (needsNewCode) return;
          void handleVerify(value);
        }}
        length={CODE_LENGTH}
        invalid={Boolean(error)}
        busy={isVerifying}
        label="Enter the code"
        // The heading above already says what this is and where it went, so the
        // label is for screen readers only and the description carries errors
        // alone rather than restating it.
        visuallyHideLabel
        description={error ?? undefined}
        autoFocus
      />

      <div className="flex flex-col gap-2.5">
        <p className="text-sm text-text-secondary dark:text-white/55">
          Didn't get the OTP?{" "}
          {canResend ? null : (
            <span className="tabular-nums">
              (Request again in 0:{String(resendIn).padStart(2, "0")}s)
            </span>
          )}
        </p>

        {/*
          A single resend affordance. The reference has SMS and WhatsApp chips;
          only SMS exists here, and offering a WhatsApp button that does nothing
          would be worse than not offering it.

          Disabled rather than hidden while the timer runs, so the control does
          not appear from nowhere the moment it becomes usable.
        */}
        <div>
          <button
            type="button"
            disabled={isSending || !canResend}
            onClick={() => {
              if (!canonicalNumber) return;
              // A spent code (the collision case) needs a genuinely new one;
              // every other resend re-sends what was already generated, so the
              // SMS already in their inbox stays valid.
              void (needsNewCode
                ? sendCode(canonicalNumber)
                : resendCode(canonicalNumber));
            }}
            className="inline-flex h-9 items-center gap-2 rounded-full bg-surface-alt px-4 text-sm font-medium text-text-primary transition-colors hover:bg-surface-elevated disabled:opacity-45 dark:bg-white/[0.08] dark:text-white dark:hover:bg-white/[0.14]"
          >
            {isSending ? (
              <Spinner className="size-4" />
            ) : (
              <MessageSquare className="size-4" aria-hidden="true" />
            )}
            SMS
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 pt-1">
        <button
          type="button"
          onClick={() => {
            // Back to the number, not out of the flow. Clears the code so a
            // stale value cannot be submitted against a newly entered number.
            setStep("number");
            setCode("");
            setError(null);
            setNeedsNewCode(false);
          }}
          className="self-center text-sm text-text-primary underline decoration-dotted underline-offset-4 transition-opacity hover:opacity-70 dark:text-white"
        >
          {mode.kind === "attach" ? "Use a different number" : "Back to login"}
        </button>

        <Button
          type="button"
          disabled={isVerifying || code.length !== CODE_LENGTH || needsNewCode}
          onClick={() => void handleVerify(code)}
          className="h-14 w-full rounded-full bg-brand text-base font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-45 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
        >
          {isVerifying ? <Spinner className="size-5" /> : "Continue"}
        </Button>
      </div>

      {footer}
    </div>
  );
}
